import { TaskType, type ToWorkerMessage } from "Worker/entities";
import { ClientApp } from "target_wasm";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { readClipboardMeta } from "components/canvas/utils/clipboard";

// `handle_paste` rejects with `JsValue::from(&str)`, which reaches JS as a bare
// string rather than an Error — those carry the actionable copy (e.g. how to
// paste multiline content), so surface them instead of a generic failure.
export function pasteErrorMessage(error: unknown): string {
  if (typeof error === "string" && error.trim() !== "") return error;
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  return "Paste failed";
}

async function handlePaste(
  event: ClipboardEvent,
  clientApp: ClientApp,
  worker: Worker,
  modelID?: string,
) {
  if (document.activeElement?.id === "Main-Canvas") {
    event.preventDefault();

    const clip = navigator.clipboard;
    const items = await clip.read();

    for (const item of items) {
      if (item.types.includes("text/plain")) {
        const blob = await item.getType("text/plain");
        const text_data = await blob.text();

        const mycorr_meta = await readClipboardMeta(item, text_data);

        if (mycorr_meta) {
          // MycorrCopyMeta paste (cell, table view, column) — synchronous in WASM
          try {
            clientApp?.handle_paste(text_data, mycorr_meta);
          } catch (error) {
            showNotificationGlobal(pasteErrorMessage(error), "error");
          }
        } else if (!clientApp.should_paste_create_table()) {
          // Single cell focused — cell paste in WASM
          try {
            clientApp?.handle_paste(text_data, null);
          } catch (error) {
            showNotificationGlobal(pasteErrorMessage(error), "error");
          }
        } else if (modelID) {
          // Multi-row text → fast route via CSV sniffer (auto-detects TSV and CSV)
          try {
            const tableName = clientApp.next_default_table_name();
            const encoder = new TextEncoder();
            const textBytes = encoder.encode(text_data);
            const sampleBytes = textBytes.buffer.slice(
              0,
              Math.min(1_000_000, textBytes.byteLength),
            );

            showNotificationGlobal(
              "Importing pasted data...",
              "default",
              undefined,
              0,
            );

            const msg: ToWorkerMessage = {
              task: TaskType.DsvFastImport,
              data: {
                sampleBytes,
                fileBytes: textBytes.buffer as ArrayBuffer,
                modelID,
                filename: tableName,
                source: "paste",
              },
            };
            worker.postMessage(msg);
          } catch (_error) {
            showNotificationGlobal("Paste failed", "error");
          }
        }
      }
      break;
    }
  }
}

export { handlePaste };
